import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Building2, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

const LOGO_URL = 'https://media.base44.com/images/public/69e7659143303e8153678f84/d1f090528_Asset1.png';

const SETUP_STEPS = [
  { n: '1', title: 'Register your details', desc: 'Create your Motion Reality login using the email this invite was sent to.' },
  { n: '2', title: 'Verify your email', desc: 'Enter the one-time code we send to your inbox to activate your account.' },
  { n: '3', title: "We'll activate your trade portal", desc: 'Your trade account links automatically — no code needed.' },
];

// Dedicated 'You've Been Invited' screen shown when a logged-out visitor
// arrives at /b2b?invite=<token>. Looks up the company name from the token,
// then presents a prominent 'Set Up Your Account' button that routes into the
// app's own registration flow (NOT the hosted sign-in page, which traps new
// invitees with an 'Invalid email or password' error). After registration the
// user is sent back to /b2b?invite=<token> so the invite is accepted.
export default function InvitedScreen({ token, onSignIn }) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    base44.functions.invoke('b2bLookupInvite', { token })
      .then((res) => {
        if (!alive) return;
        if (res?.data?.success) {
          setCompanyName(res.data.company_name || '');
          setContactEmail(res.data.contact_email || '');
        } else {
          setError(res?.data?.error || 'This invite link is not valid.');
        }
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.error || e.message || 'Could not look up this invitation.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  // Stash the invite token so it survives the jump to /account, then send the
  // user into the app's registration flow with their email pre-filled.
  const handleSetUp = () => {
    try { if (token) localStorage.setItem('b2b_invite_token', token); } catch (_e) { /* localStorage unavailable */ }
    const params = new URLSearchParams();
    params.set('mode', 'register');
    if (contactEmail) params.set('email', contactEmail);
    navigate(`/account?${params.toString()}`);
  };

  // For users who already have a Motion Reality login — route into the app's
  // sign-in (preserves the stashed invite token for the return trip).
  const handleSignIn = () => {
    try { if (token) localStorage.setItem('b2b_invite_token', token); } catch (_e) { /* localStorage unavailable */ }
    navigate('/account?mode=signin');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-yellow-400/30 bg-yellow-400/[0.03] p-8 text-center">
        <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
        <p className="text-white font-bold text-sm">{error}</p>
        <button onClick={() => navigate('/account?mode=signin')} className="inline-flex items-center gap-1.5 mt-4 text-primary text-xs font-bold tracking-widest hover:underline">
          SIGN IN <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="border border-primary/20 bg-primary/[0.03] p-8 sm:p-12 text-center">
      <img src={LOGO_URL} alt="Motion Reality" className="h-10 mx-auto mb-6" />
      <p className="text-primary text-xs font-bold tracking-widest mb-3">TRADE ACCOUNT INVITATION</p>
      <h2 className="text-white font-black text-xl sm:text-2xl uppercase mb-3 leading-tight">
        You've been invited to set up a trade account
      </h2>
      {companyName && (
        <div className="inline-flex items-center gap-2 border border-white/15 bg-black/30 px-4 py-2 mb-6">
          <Building2 className="w-4 h-4 text-primary" />
          <span className="text-white/80 text-sm font-bold">{companyName}</span>
        </div>
      )}
      <p className="text-white/50 text-sm leading-relaxed max-w-md mx-auto mb-8">
        Setting up takes less than two minutes. Here's how it works:
      </p>

      {/* Numbered setup steps */}
      <div className="max-w-md mx-auto mb-8 space-y-4 text-left">
        {SETUP_STEPS.map((step) => (
          <div key={step.n} className="flex gap-3">
            <div className="w-6 h-6 shrink-0 border border-primary/40 text-primary text-xs font-black flex items-center justify-center mt-0.5">
              {step.n}
            </div>
            <div>
              <p className="text-white text-sm font-bold">{step.title}</p>
              <p className="text-white/40 text-xs leading-relaxed mt-0.5">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleSetUp}
        className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest px-8 py-4 transition-colors"
      >
        SET UP YOUR ACCOUNT <ArrowRight className="w-4 h-4" />
      </button>
      <div className="mt-6">
        <button
          onClick={handleSignIn}
          className="text-white/40 hover:text-white/70 text-xs font-bold tracking-widest underline-offset-4 hover:underline transition-colors"
        >
          Already registered? Sign in
        </button>
      </div>
    </div>
  );
}